import {Injectable, inject, DestroyRef} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {ConfigurationService} from 'src/app/services/api/configuration.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {SessionManagementService} from '../util/session-management.service';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';

declare const kony: any;

const defaultHeaders = {
  'Content-Type': 'application/json',
  // TODO: Used by engine. To be removed when Engine starts reading it from JWT token
  'X-Temenos-TenantId': 'tenant1',
};

@Injectable({
  providedIn: 'root'
})
export class FabricService  {
  private destroyRef = inject(DestroyRef);

  public expiryInactive = new BehaviorSubject<any>([]);
  private client: any;
  private identityService: any;
  private tenant;
  private invalidToken = false;
  constructor(private configurationService: ConfigurationService, private sessionsService: SessionManagementService,
     private router: Router
  ) {
    
    this.tenant = window.location.pathname.split('/')[1].split('?')[0];
    defaultHeaders['X-Temenos-TenantId'] = this.tenant;
  }

  initialize(useDefaultClient?: boolean): Observable<any> {
    let result;
    if (useDefaultClient) {
      this.client = kony.sdk.getDefaultInstance();
      result = of(true);
    } else {
      result = new Subject();
      this.client = new kony.sdk();
      this.configurationService.getConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((configuration) => {
        this.client.init(configuration.appKey, configuration.appSecret, configuration.serviceUrl,
          ...this.subjectToFunctions(result));
      });
    }
    return result;
  }

  login(): Observable<any> {
    // Remove query params since they break single page login
    const successUrl = `${window.location.origin}${window.location.pathname}`;
    const options = {
      loginOptions: {
        noPopup: true,
        channel: 'plainJS',
        customQueryParamsForOAuth: {success_url: successUrl}
      }
    } as any;
    const result = new Subject();
    // TODO: noPopup is not working. Why?
    this.getIdentityService().login(options, ...this.subjectToFunctions(result));
    return result;
  }


refreshClaimsToken() {
  return new Promise((resolve, reject) => {
    // First param: isBackendTokenRefreshRequired (boolean)
    // Second param: success callback
    // Third param: failure callback
    kony.sdk.fetchClaimsTokenFromServer(
      true,
      (response) => {
        const diffInMs = response.claims_token.exp - new Date().getTime();
        this.expiryInactive.next(diffInMs - 30000);
        // Handle success
        resolve(response);
      },
      (error) => {
        // Handle failure
        reject(error);
      }
    );
  });
}


  /**
   * This functionality is quire confusing.
   * If there is URL query parameter called code this function should be called first.
   * It will instantiate kony sdk, so after it completes this.client should be set to kony.sdk.getDefaultInstance()
   * instead of new kony.sdk().
   * Also in this case normal login should be skipped.
   */
  performNoPopUpLogin(): Observable<any> {
    const result = new Subject();
    kony.sdk.performNoPopUpLogin(...this.subjectToFunctions(result));
    return result;
  }

  logout(): Observable<any> {
    const result = new Subject();
    this.getIdentityService().logout(...this.subjectToFunctions(result));
    return result;
  }

  invokeOperation<R>(serviceName: string, operationName: string, params?, headers?): Observable<R> {
    const result = new Subject<R>();
    this.client.getIntegrationService(serviceName)
      .invokeOperation(operationName, {...defaultHeaders, ...headers}, params, ...this.subjectToFunctions(result));
    return result.pipe(map(response => {
        if ((response as any).Records) {
          return (response as any).Records;
        }
        return response;
      }), tap(() => {
        if (this.sessionsService.sessionExpirationEpoch !== this.getClient().claimTokenExpiry / 1000) {
          this.sessionsService.sessionExpirationEpoch = this.getClient().claimTokenExpiry / 1000;
          // const d: any = new Date(0);
          // d.setUTCSeconds(this.sessionsService.sessionExpirationEpoch);
          // const date2 = new Date();

          // const diffInMs = this.getClient().claimTokenExpiry - new Date().getTime();
          // this.expiryInactive.next(diffInMs - 30000);
        }
      })

      , catchError((error) => {
        // "errcode":17005,"errmsg":"Not authorized"
        // -55, Session Invalid
        // 104, Session/Token got invalidated in the backend.Please login.
        if (error && (
          [17005, -55, 104].includes((error as any).errcode)
          || [17005, -55, 104].includes((error as any).opstatus)
          || [17005, -55, 104].includes((error as any).code)
        )) {
          window.location.reload();
          return of(error);
        }
        return throwError(error);
      }));
  }

  getClient(): any {
    // TODO: Use getter in case that reconnect/reinit is needed in the future, which can be added here
    return this.client;
  }

   getIdentityService(): any {
    if (!this.identityService) {
      this.identityService = this.getClient().getIdentityService(this.tenant);
    }
    return this.identityService;
  }

  private subjectToFunctions(subject: Subject<any>): any {
    return [(success): void => {
      subject.next(success);
      subject.complete();
    }, (error): void => {
      subject.error(error);
      this.invalidToken = true;
    }];
  }
}
